import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Download, XCircle, FileSpreadsheet, FileJson, FileType } from 'lucide-react';
import { getModuleEntity, getModuleWorkstreams } from '../../config/moduleHelpers';

const DownloadModal = ({ selectedClients, staffList, config, onClose }) => {
    const [format, setFormat] = useState('excel');
    const entity = getModuleEntity(config);
    const workstreams = getModuleWorkstreams(config);
    const mainField = config?.fieldMappings?.mainField || 'name';
    const numberField = config?.fieldMappings?.numberField || 'number';
    const [sections, setSections] = useState({
        entity: true,
        contact: true,
        notes: false,
        due_dates: false,
        work_plan: false,
        assignments: false,
        status: false
    });

    const EXPORT_CONFIG = useMemo(() => ({
        entity: { label: `${entity.singular} Details`, fields: [mainField, numberField], headers: [`${entity.singular} Name`, entity.numberLabel] },
        contact: { label: 'Contact Details', fields: ['pc_fname', 'pc_pname', 'pc_email', 'pc_tel'], headers: ['Contact Name', 'Pref Name', 'Email', 'Phone'] },
        notes: { label: 'Notes', fields: ['notes'], headers: ['Notes'] },
        due_dates: { label: 'Due Dates', fields: workstreams.filter(stream => stream.dueField).map(stream => stream.dueField), headers: workstreams.filter(stream => stream.dueField).map(stream => `${stream.shortLabel} Due`) },
        work_plan: { label: 'Work Plan Details', fields: workstreams.filter(stream => stream.planField).map(stream => stream.planField), headers: workstreams.filter(stream => stream.planField).map(stream => `${stream.shortLabel} Plan`) },
        assignments: { label: 'Work Assignments', fields: workstreams.filter(stream => stream.staffField).map(stream => stream.staffField), headers: workstreams.filter(stream => stream.staffField).map(stream => `${stream.shortLabel} Staff`) },
        status: { label: 'Work Status', fields: workstreams.filter(stream => stream.statusField).map(stream => stream.statusField), headers: workstreams.filter(stream => stream.statusField).map(stream => `${stream.shortLabel} Status`) }
    }), [entity.singular, entity.numberLabel, mainField, numberField, workstreams]);

    const toggleSection = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));
    const toggleAll = (val) => {
        const newSections = {};
        Object.keys(sections).forEach(k => newSections[k] = val);
        setSections(newSections);
    };

    const staffMap = useMemo(() => {
        return (staffList || []).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {});
    }, [staffList]);

    const getFieldValue = (client, field) => {
        if (!client) return '';

        if (workstreams.some(stream => stream.staffField === field)) {
            return staffMap[client[field]] || '';
        }

        return client[field] ?? '';
    };

    const handleDownload = () => {
        const activeSections = Object.keys(sections).filter(k => sections[k]);
        let headers = [];
        activeSections.forEach(k => headers = [...headers, ...EXPORT_CONFIG[k].headers]);

        const rows = selectedClients.map(client => {
            let rowData = [];
            activeSections.forEach(k => {
                const fields = EXPORT_CONFIG[k].fields;
                rowData = [...rowData, ...fields.map(f => getFieldValue(client, f))];
            });
            return rowData;
        });

        if (format === 'excel') {
            let tableContent = `<html><head><meta charset="utf-8"></head><body><table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
            rows.forEach(row => tableContent += `<tr>${row.map(c => `<td>${c}</td>`).join('')}</tr>`);
            tableContent += `</tbody></table></body></html>`;
            
            const blob = new Blob([tableContent], { type: 'application/vnd.ms-excel' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${entity.plural.toLowerCase().replace(/\s+/g, '-')}.xls`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'csv') {
                        const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${entity.plural.toLowerCase().replace(/\s+/g, '-')}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
                } else if (format === 'pdf') {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) return;

                        const tableContent = `
                            <html>
                                <head>
                                    <title>${entity.singular} Export</title>
                                    <style>
                                        body { font-family: Arial, sans-serif; font-size: 11px; }
                                        table { width: 100%; border-collapse: collapse; }
                                        th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
                                        th { background: #f0f0f0; }
                                    </style>
                                </head>
                                <body>
                                    <h2>${entity.singular} Export</h2>
                                    <table>
                                        <thead>
                                            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
                                        </thead>
                                        <tbody>
                                            ${rows.map(row => `<tr>${row.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`).join('')}
                                        </tbody>
                                    </table>
                                </body>
                            </html>
                        `;

                        printWindow.document.write(tableContent);
                        printWindow.document.close();
                        printWindow.focus();
                        printWindow.print();
        }
        
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <h3 className="text-lg font-bold text-slate-800">Download Options</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XCircle className="w-6 h-6" /></button>
                </div>
                
                <div className="p-6 space-y-6">
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 uppercase">File Format</label>
                        <div className="grid grid-cols-3 gap-3">
                            <button onClick={() => setFormat('excel')} className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${format === 'excel' ? 'border-green-500 bg-green-50 text-green-700 ring-1 ring-green-500' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-700'}`}>
                                <FileSpreadsheet className="w-4 h-4" /> Excel
                            </button>
                            <button onClick={() => setFormat('csv')} className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${format === 'csv' ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-700'}`}>
                                <FileJson className="w-4 h-4" /> CSV
                            </button>
                            <button onClick={() => setFormat('pdf')} className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${format === 'pdf' ? 'border-red-500 bg-red-50 text-red-700 ring-1 ring-red-500' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-700'}`}>
                                <FileType className="w-4 h-4" /> PDF
                            </button>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-slate-500 uppercase">Include Sections</label>
                            <div className="flex gap-2 text-xs">
                                <button onClick={() => toggleAll(true)} className="text-blue-600 hover:underline">Select All</button>
                                <span className="text-slate-300">|</span>
                                <button onClick={() => toggleAll(false)} className="text-slate-500 hover:underline">None</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {Object.entries(EXPORT_CONFIG).map(([key, config]) => (
                                <label key={key} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                                    <input type="checkbox" checked={sections[key]} onChange={() => toggleSection(key)} className="w-4 h-4" />
                                    <span className="text-sm text-slate-700 font-medium">{config.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg">Cancel</button>
                    <button onClick={handleDownload} className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
                        <Download className="w-4 h-4" /> Download
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DownloadModal;